import { Link } from '@tanstack/react-router';
import type { CSSProperties, PointerEventHandler, RefObject } from 'react';

import { Icon } from '#/components/ui/icons';
import { Spinner } from '#/components/ui/Spinner';
import { Ticker } from '#/components/ui/Ticker';
import { pushEvent } from '#/lib/gtm';
import { cn } from '#/utils/cn';
import { formatCurrency } from '#/utils/format';

import type { SearchPreviewState } from './searchPagination';
import { buildSearchState } from './searchPagination';
import type {
  SearchPreviewGroup,
  SearchPreviewInvestmentType,
  SearchPreviewTopResult,
} from './useSearchPreview';

const DETAIL_ROUTE_CATEGORY = {
  equity: 'equities',
  etf: 'etfs',
  mutual_fund: 'mutual-funds',
} as const;

type FloatingPropGetter = (
  userProps?: Record<string, unknown>,
) => Record<string, unknown>;

interface SearchPreviewPopoverProps {
  setFloating: (node: HTMLDivElement | null) => void;
  floatingStyles: CSSProperties;
  transitionStyles: CSSProperties;
  getFloatingProps: FloatingPropGetter;
  getItemProps: FloatingPropGetter;
  open: boolean;
  query: string;
  /** Total selectable suggestions shown; sent as `result_count`. */
  previewItemCount: number;
  /** Live SPA route path for `page_path` on selection events. */
  pagePath: string;
  searchState?: SearchPreviewState;
  topResult?: SearchPreviewTopResult | null;
  groups: SearchPreviewGroup[];
  hasPreviewResults: boolean;
  isRefreshing: boolean;
  showLoadingOnlyState: boolean;
  showEmptyState: boolean;
  activeIndex: number | null;
  itemRefs: RefObject<Array<HTMLElement | null>>;
  onPointerDownCapture?: PointerEventHandler<HTMLDivElement>;
  onClose: () => void;
}

export function SearchPreviewPopover({
  setFloating,
  floatingStyles,
  transitionStyles,
  getFloatingProps,
  getItemProps,
  open,
  query,
  previewItemCount,
  pagePath,
  searchState,
  topResult,
  groups,
  hasPreviewResults,
  isRefreshing,
  showLoadingOnlyState,
  showEmptyState,
  activeIndex,
  itemRefs,
  onPointerDownCapture,
  onClose,
}: SearchPreviewPopoverProps) {
  let itemIndex = 0;

  const getNextItemIndex = () => {
    const nextIndex = itemIndex;
    itemIndex += 1;
    return nextIndex;
  };

  const topResultItemIndex = topResult?.ticker ? getNextItemIndex() : null;
  const viewAllItemIndex = hasPreviewResults ? getNextItemIndex() : null;

  return (
    <div
      ref={setFloating}
      className={cn('z-60', !open && 'pointer-events-none')}
      style={floatingStyles}
      onPointerDownCapture={onPointerDownCapture}
    >
      <div
        className="motion-reduce:transform-none motion-reduce:transition-none"
        style={transitionStyles}
      >
        <div
          className="border-blue-20 bg-gray-10 relative max-h-full overflow-y-auto rounded-lg border p-8 shadow-(--shadow-card)"
          {...getFloatingProps({
            'aria-label': 'Search preview results',
            'aria-busy': isRefreshing,
          })}
        >
          {showLoadingOnlyState ? (
            <div className="flex min-h-96 items-center justify-center px-12 py-16">
              <Spinner label={`Searching for “${query}”…`} />
            </div>
          ) : (
            <div
              data-search-preview-content="true"
              className={cn(
                'transition-opacity duration-150',
                isRefreshing && 'opacity-60',
              )}
            >
              {topResult ? (
                <SearchPreviewResultRow
                  itemIndex={topResultItemIndex}
                  isActive={
                    topResultItemIndex != null &&
                    activeIndex === topResultItemIndex
                  }
                  itemRefs={itemRefs}
                  getItemProps={getItemProps}
                  result={{
                    investment_type: topResult.investment_type,
                    ticker: topResult.ticker ?? null,
                    name: topResult.name ?? null,
                  }}
                  resultKind="top_result"
                  previewItemCount={previewItemCount}
                  query={query}
                  pagePath={pagePath}
                  onNavigate={onClose}
                />
              ) : null}

              {hasPreviewResults ? (
                <Link
                  {...getItemProps({
                    active:
                      viewAllItemIndex != null &&
                      activeIndex === viewAllItemIndex,
                    selected: false,
                    tabIndex: -1,
                    ref(node: HTMLElement | null) {
                      if (viewAllItemIndex == null) {
                        return;
                      }

                      itemRefs.current[viewAllItemIndex] = node;
                    },
                  })}
                  to="/search"
                  search={buildSearchState({
                    query,
                    investment_type: searchState?.investment_type,
                    sort_by: searchState?.sort_by,
                    order_by: searchState?.order_by,
                    equity_sector_slug: searchState?.equity_sector_slug,
                    fund_category_slug: searchState?.fund_category_slug,
                    listing_type: searchState?.listing_type,
                    exchange: searchState?.exchange,
                  })}
                  className={cn(
                    'rounded-12 hover:bg-steel-10 border-slate-20 text-static-14 flex items-center gap-8 border-b px-12 py-16 font-semibold uppercase no-underline transition-colors',
                    activeIndex === viewAllItemIndex && 'bg-steel-10',
                  )}
                  onClick={() => {
                    pushEvent({
                      event: 'mdc_search_result_select',
                      result_position: viewAllItemIndex ?? 0,
                      result_count: previewItemCount,
                      result_kind: 'view_all',
                      query_length: query.length,
                      href: '/search',
                      page_path: pagePath,
                    });
                    onClose();
                  }}
                >
                  <span>{`View all results for “${query}”`}</span>
                  <Icon id="arrow-right" size={16} className="shrink-0" />
                </Link>
              ) : null}

              {groups.map((group) =>
                group.results.length > 0 ? (
                  <section key={group.investmentType} className="px-4 pt-16">
                    <h2 className="text-link-fg text-static-14 px-8 pb-8 font-semibold">
                      {group.label}
                    </h2>
                    <div>
                      {group.results.map((result) => {
                        const resultItemIndex =
                          result.ticker != null ? getNextItemIndex() : null;

                        return (
                          <SearchPreviewResultRow
                            key={`${group.investmentType}-${result.ticker ?? result.name}`}
                            itemIndex={resultItemIndex}
                            isActive={
                              resultItemIndex != null &&
                              activeIndex === resultItemIndex
                            }
                            itemRefs={itemRefs}
                            getItemProps={getItemProps}
                            result={{
                              investment_type: group.investmentType,
                              ticker: result.ticker ?? null,
                              name: result.name ?? null,
                              price: result.price ?? null,
                            }}
                            resultKind="suggestion_row"
                            previewItemCount={previewItemCount}
                            query={query}
                            pagePath={pagePath}
                            onNavigate={onClose}
                          />
                        );
                      })}
                    </div>
                  </section>
                ) : null,
              )}

              {showEmptyState ? (
                <p className="text-fg-subheader text-static-14 px-12 py-16">
                  {`No results found for “${query}”.`}
                </p>
              ) : null}
            </div>
          )}

          {!showLoadingOnlyState ? (
            <div
              aria-hidden={!isRefreshing}
              className={cn(
                'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity',
                isRefreshing ? 'opacity-100' : 'invisible opacity-0',
              )}
            >
              <Spinner label={`Refreshing search results for “${query}”…`} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

type SearchPreviewResult = {
  investment_type: SearchPreviewInvestmentType;
  ticker?: string | null;
  name?: string | null;
  price?: number | null;
};

function SearchPreviewResultRow({
  itemIndex,
  isActive,
  itemRefs,
  getItemProps,
  result,
  resultKind,
  previewItemCount,
  query,
  pagePath,
  onNavigate,
}: {
  itemIndex: number | null;
  isActive: boolean;
  itemRefs: RefObject<Array<HTMLElement | null>>;
  getItemProps: FloatingPropGetter;
  result: SearchPreviewResult;
  resultKind: 'top_result' | 'suggestion_row';
  previewItemCount: number;
  query: string;
  pagePath: string;
  onNavigate: () => void;
}) {
  const detailParams =
    result.ticker != null
      ? {
          category: DETAIL_ROUTE_CATEGORY[result.investment_type],
          id: result.ticker,
        }
      : null;

  const content = (
    <>
      <div className="flex min-w-0 flex-1 items-center">
        <p className="text-fg-header text-static-16 flex items-center gap-8 truncate leading-snug font-semibold">
          {result.ticker ? (
            <span className="text-slate-60">
              <Ticker symbol={result.ticker} size="sm" />
            </span>
          ) : null}
          {result.name ?? result.ticker ?? 'Search result'}
        </p>
      </div>
      {result.price != null ? (
        <span className="text-fg-header text-static-16 shrink-0 font-semibold">
          {formatCurrency(result.price)}
        </span>
      ) : null}
    </>
  );

  const sharedClassName =
    'rounded-12 hover:bg-steel-10 flex items-center gap-16 border-b border-slate-20 px-12 py-16 no-underline transition-colors last:border-b-0';

  return detailParams ? (
    <Link
      {...getItemProps({
        active: isActive,
        selected: false,
        tabIndex: -1,
        ref(node: HTMLElement | null) {
          if (itemIndex == null) {
            return;
          }

          itemRefs.current[itemIndex] = node;
        },
      })}
      to="/$category/$id"
      params={detailParams}
      className={cn(sharedClassName, isActive && 'bg-steel-10')}
      onClick={() => {
        pushEvent({
          event: 'mdc_search_result_select',
          result_position: itemIndex ?? 0,
          result_count: previewItemCount,
          result_id: result.ticker ?? result.name ?? null,
          result_type: result.investment_type,
          result_kind: resultKind,
          query_length: query.length,
          href: '/$category/$id',
          page_path: pagePath,
        });
        onNavigate();
      }}
    >
      {content}
    </Link>
  ) : (
    <div className={sharedClassName}>{content}</div>
  );
}
